import { create } from "zustand";

/** A roster entry as tracked by the moderation panel (normalized from Jitsi iframe events). */
type ModerationParticipant = {
  id: string;
  displayName: string;
  isLocal: boolean;
  isModerator: boolean;
};

type ModerationParticipantPatch = Partial<ModerationParticipant> & {
  id: string;
};

type MediaType = "audio" | "video";

/** One occupant of a breakout room, normalized from the `breakoutRoomsUpdated` event. */
type BreakoutRoomParticipant = {
  // Endpoint id (the resource part of the occupant nick JID). Used for self-detection against
  // `localParticipantId`, as the React key, and as the display-name fallback.
  id: string;
  // The FULL occupant nick JID (the `breakoutRoomsUpdated` map KEY, `<room-node>@<muc-domain>/
  // <endpointId>`). This — NOT the endpoint id — is what the `sendParticipantToRoom` external-API
  // command must be given for an occupant who lives in a breakout room: its `_findParticipantJid`
  // (react/features/breakout-rooms/actions.ts, verified @ stable/jitsi-meet_11031) only key-matches
  // the redux breakout participants map when the value contains "@"; a bare endpoint id falls
  // through to `conference.getParticipantById()` on the moderator's OWN conference, which never
  // holds breakout occupants → the move silently no-ops. "" when the key carried no "@" (defensive;
  // never observed in the verified payload).
  jid: string;
  displayName: string;
};

/**
 * A breakout room as normalized from the Jitsi iframe's `breakoutRoomsUpdated` event.
 * Shape VERIFIED against stable/jitsi-meet_11031 (prosody mod_muc_breakout_rooms.lua
 * `broadcast_breakout_rooms` → react/features/breakout-rooms/middleware.ts → API.js
 * `notifyBreakoutRoomsUpdated`).
 *
 * `id` is the MAP KEY of that event's payload — the room NODE (main room = its name; breakout
 * rooms = a random uuid), equal to the payload's own `id` field. The redux handlers behind the
 * `sendParticipantToRoom` / `closeBreakoutRoom` external-API commands resolve their target via
 * `rooms[roomId]` against the SAME map, so the map key is by construction the value those
 * commands expect.
 *
 * `jid` is the room's full MUC JID (breakout rooms live on the breakout MUC domain,
 * `<node>@breakout.<domain>`; the main room's jid is on the conference MUC domain,
 * `<node>@conference.<domain>`) — the identifier the
 * `removeBreakoutRoom` command requires (it resolves via `getRoomByJid`, NOT the map key).
 * "" when the payload omitted it (never observed in the verified shape; defensive only).
 *
 * The main room is kept in this list (flagged `isMainRoom`) so callers can decide whether to
 * display it.
 */
type BreakoutRoom = {
  id: string;
  jid: string;
  name: string;
  isMainRoom: boolean;
  participants: BreakoutRoomParticipant[];
};

type CallModerationState = {
  participants: ModerationParticipant[];
  localParticipantId: string | null;
  localIsModerator: boolean;
  // True once the LOCAL participant's role has actually been observed (from the
  // `videoConferenceJoined` payload's `role`, or a `participantRoleChanged` targeting the local id).
  // Distinguishes "confirmed non-moderator" from "role not yet known" — the moderator-only toolbar
  // button is only removed once this is true AND `localIsModerator` is false, so a moderator can
  // never lose the button while their role is still unknown (see `useModerationButtonVisibility`).
  localRoleKnown: boolean;
  // Observed moderator role per participant id, recorded by `setParticipantRole` regardless of
  // whether that id is (yet) the local participant. This makes local-role resolution ORDER-
  // INDEPENDENT: the external API is not guaranteed to deliver the local `participantRoleChanged`
  // AFTER `videoConferenceJoined` (it may arrive first, while `localParticipantId` is still null),
  // so we buffer every role here and reconcile it in `setLocalParticipantId`. Without this a
  // role-before-join sequence would leave `localRoleKnown` stuck false and the moderator-only
  // toolbar button would never be hidden from a confirmed non-moderator.
  rolesById: Record<string, boolean>;
  moderationOn: Record<MediaType, boolean>;
  // Current breakout rooms, mirrored from the iframe's `breakoutRoomsUpdated` event. Read-only
  // state — the moderator-only actions live in `BreakoutRoomsSection`; a non-moderator can still
  // receive this event (it just populates state), but never renders the section or issues a command.
  breakoutRooms: BreakoutRoom[];
  isModerationPanelOpen: boolean;
  // Visitor/webinar tier is Phase 5 and not built yet (moderation-panel-design.md §5, §8).
  // Defaults to false so every existing (non-webinar) call is provably unaffected.
  isWebinarMode: boolean;
};

type Actions = {
  setLocalParticipantId: (id: string | null) => void;
  setLocalIsModerator: (value: boolean) => void;
  upsertParticipant: (patch: ModerationParticipantPatch) => void;
  removeParticipant: (id: string) => void;
  setParticipantRole: (id: string, isModerator: boolean) => void;
  setModerationOn: (mediaType: MediaType, enabled: boolean) => void;
  setBreakoutRooms: (rooms: BreakoutRoom[]) => void;
  setModerationPanelOpen: (open: boolean) => void;
  toggleModerationPanel: () => void;
  setWebinarMode: (value: boolean) => void;
  resetModerationState: () => void;
};

const initialState: CallModerationState = {
  participants: [],
  localParticipantId: null,
  localIsModerator: false,
  localRoleKnown: false,
  rolesById: {},
  moderationOn: { audio: false, video: false },
  breakoutRooms: [],
  isModerationPanelOpen: false,
  isWebinarMode: false,
};

export const useCallModerationState = create<CallModerationState & Actions>()(
  (set, get) => ({
    ...initialState,

    setLocalParticipantId(id) {
      set((state) => {
        // Reconcile an already-observed role for this id (role-before-join ordering): if the local
        // participant's role arrived before we knew our own id, adopt it now so `localRoleKnown`
        // flips and the moderator-only toolbar button can be resolved. If no role was observed yet
        // the role stays unknown (button stays — status quo) until a `participantRoleChanged` lands.
        if (
          id !== null &&
          Object.prototype.hasOwnProperty.call(state.rolesById, id)
        ) {
          return {
            localParticipantId: id,
            localIsModerator: state.rolesById[id],
            localRoleKnown: true,
          };
        }
        return { localParticipantId: id };
      });
    },
    setLocalIsModerator(value) {
      set({ localIsModerator: value });
    },
    upsertParticipant(patch) {
      set((state) => {
        const index = state.participants.findIndex((p) => p.id === patch.id);
        if (index === -1) {
          return {
            participants: [
              ...state.participants,
              {
                id: patch.id,
                displayName: patch.displayName ?? patch.id,
                isLocal: patch.isLocal ?? false,
                isModerator: patch.isModerator ?? false,
              },
            ],
          };
        }
        const next = [...state.participants];
        next[index] = { ...next[index], ...patch };
        return { participants: next };
      });
    },
    removeParticipant(id) {
      set((state) => {
        // Drop the buffered role too, so `rolesById` tracks the live roster (not unbounded churn).
        const { [id]: _removed, ...rolesById } = state.rolesById;
        return {
          participants: state.participants.filter((p) => p.id !== id),
          rolesById,
        };
      });
    },
    setParticipantRole(id, isModerator) {
      set((state) => ({
        participants: state.participants.map((p) =>
          p.id === id ? { ...p, isModerator } : p,
        ),
        // Buffer the role for this id regardless of whether it is the local participant yet — this
        // is what makes local-role resolution order-independent (see `rolesById` / the reconcile in
        // `setLocalParticipantId` for the role-before-join case).
        rolesById: { ...state.rolesById, [id]: isModerator },
      }));
      // The local participant's own role drives `localIsModerator`, which gates the whole panel.
      // Observing it here (from `videoConferenceJoined`'s role or a local `participantRoleChanged`)
      // is also the single point where the local role becomes KNOWN — for the role-AFTER-join case.
      if (get().localParticipantId === id) {
        set({ localIsModerator: isModerator, localRoleKnown: true });
      }
    },
    setModerationOn(mediaType, enabled) {
      set((state) => ({
        moderationOn: { ...state.moderationOn, [mediaType]: enabled },
      }));
    },
    setBreakoutRooms(rooms) {
      set({ breakoutRooms: rooms });
    },
    setModerationPanelOpen(open) {
      set({ isModerationPanelOpen: open });
    },
    toggleModerationPanel() {
      set((state) => ({
        isModerationPanelOpen: !state.isModerationPanelOpen,
      }));
    },
    setWebinarMode(value) {
      set({ isWebinarMode: value });
    },
    resetModerationState() {
      set(initialState);
    },
  }),
);

export type {
  ModerationParticipant,
  ModerationParticipantPatch,
  MediaType,
  BreakoutRoom,
  BreakoutRoomParticipant,
};
